import configureMeasurements, { Measure, Unit } from 'convert-units';
import allMeasures from 'convert-units/definitions/all';
import { MathUtils } from './math.utils';

export type DigitalUnits = DigitalBitsUnits | DigitalBytesUnits;
export type DigitalSystems = 'bits' | 'bytes';

export type DigitalBitsUnits = 'b' | 'Kb' | 'Kib' | 'Mb' | 'Mib' | 'Gb' | 'Gib' | 'Tb' | 'Tib';
export type DigitalBytesUnits = 'B' | 'KB' | 'KiB' | 'MB' | 'MiB' | 'GB' | 'GiB' | 'TB' | 'TiB';

const MINUTE_IN_SECONDS = 1/60
const HOUR_IN_SECONDS = MINUTE_IN_SECONDS * 1/60
const DAY_IN_SECONDS = HOUR_IN_SECONDS * 1/24
// Data-rate "per month" uses a 30-day month (a standard data-billing convention), distinct
// from the time measure's Julian average month (30.4375 d). Kept at 30 days so the published
// page content (which states the 30-day value) stays consistent with the computed table.
const MONTH_IN_SECOND = DAY_IN_SECONDS * 1 / 30

const bits: Record<DigitalBitsUnits, Unit> = {
  b: {
    name: {
      singular: 'Bit',
      plural: 'Bits',
    },
    to_anchor: 1,
  },
  Kb: {
    name: {
      singular: 'Kilobit',
      plural: 'Kilobits',
    },
    to_anchor: 1e3,
  },
  Kib: {
    name: {
      singular: 'Kibibit',
      plural: 'Kibibits',
    },
    to_anchor: 1024,
  },
  Mb: {
    name: {
      singular: 'Megabit',
      plural: 'Megabits',
    },
    to_anchor: 1e6,
  },
  Mib: {
    name: {
      singular: 'Mebibit',
      plural: 'Mebibits',
    },
    to_anchor: Math.pow(1024, 2),
  },
  Gb: {
    name: {
      singular: 'Gigabit',
      plural: 'Gigabits',
    },
    to_anchor: 1e9,
  },
  Gib: {
    name: {
      singular: 'Gibibit',
      plural: 'Gibibits',
    },
    to_anchor: Math.pow(1024, 3),
  },
  Tb: {
    name: {
      singular: 'Terabit',
      plural: 'Terabits',
    },
    to_anchor: 1e12,
  },
  Tib: {
    name: {
      singular: 'Tebibit',
      plural: 'Tebibits',
    },
    to_anchor: Math.pow(1024, 4),
  },
};

const bytes: Record<DigitalBytesUnits, Unit> = {
  B: {
    name: {
      singular: 'Byte',
      plural: 'Bytes',
    },
    to_anchor: 1,
  },
  KB: {
    name: {
      singular: 'Kilobyte',
      plural: 'Kilobytes',
    },
    to_anchor: 1e3,
  },
  KiB: {
    name: {
      singular: 'Kibibyte',
      plural: 'Kibibytes',
    },
    to_anchor: 1024,
  },
  MB: {
    name: {
      singular: 'Megabyte',
      plural: 'Megabytes',
    },
    to_anchor: 1e6,
  },
  MiB: {
    name: {
      singular: 'Mebibyte',
      plural: 'Mebibytes',
    },
    to_anchor: Math.pow(1024, 2),
  },
  GB: {
    name: {
      singular: 'Gigabyte',
      plural: 'Gigabytes',
    },
    to_anchor: 1e9,
  },
  GiB: {
    name: {
      singular: 'Gibibyte',
      plural: 'Gibibytes',
    },
    to_anchor: Math.pow(1024, 3),
  },
  TB: {
    name: {
      singular: 'Terabyte',
      plural: 'Terabytes',
    },
    to_anchor: 1e12,
  },
  TiB: {
    name: {
      singular: 'Tebibyte',
      plural: 'Tebibytes',
    },
    to_anchor: Math.pow(1024, 4),
  },
};

const measure: Measure<DigitalSystems, DigitalUnits> = {
  systems: {
    bits,
    bytes,
  },
  anchors: {
    bits: {
      bytes: {
        ratio: 1 / 8,
      },
    },
    bytes: {
      bits: {
        ratio: 8,
      },
    },
  },
};

const dataTransferRate: any = {
  systems: {
    "bit/s": {
      "bit/s": {
        name: {
          singular: 'bit per second',
          plural: 'bits per second',
        },
        to_anchor: 1,
      },
      "Kb/s": {
        name: {
          singular: 'Kilobit per second',
          plural: 'Kilobits per second',
        },
        to_anchor: 1e3,
      },
      "Kib/s": {
        name: {
          singular: 'Kibibit per second',
          plural: 'Kibibits per second',
        },
        to_anchor: 1024,
      },
      "Mb/s": {
        name: {
          singular: 'Megabit per second',
          plural: 'Megabits per second',
        },
        to_anchor: 1e6,
      },
      "Mib/s": {
        name: {
          singular: 'Mebibit per second',
          plural: 'Mebibits per second',
        },
        to_anchor: Math.pow(1024, 2),
      },
      "Gb/s": {
        name: {
          singular: 'Gigabit per second',
          plural: 'Gigabits per second',
        },
        to_anchor: 1e9,
      },
      "Gib/s": {
        name: {
          singular: 'Gibibit per second',
          plural: 'Gibibits per second',
        },
        to_anchor: Math.pow(1024, 3),
      },
      "Tb/s": {
        name: {
          singular: 'Terabit per second',
          plural: 'Terabits per second',
        },
        to_anchor: 1e12,
      },
      "Tib/s": {
        name: {
          singular: 'Tebibit per second',
          plural: 'Tebibits per second',
        },
        to_anchor: Math.pow(1024, 4),
      },
      /** Per minute */
      "bit/minute": {
        name: {
          singular: 'bit per minute',
          plural: 'bits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1,
      },
      "Kb/minute": {
        name: {
          singular: 'Kilobit per minute',
          plural: 'Kilobits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e3,
      },
      "Kib/minute": {
        name: {
          singular: 'Kibibit per minute',
          plural: 'Kibibits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1024,
      },
      "Mb/minute": {
        name: {
          singular: 'Megabit per minute',
          plural: 'Megabits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e6,
      },
      "Mib/minute": {
        name: {
          singular: 'Mebibit per minute',
          plural: 'Mebibits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 2),
      },
      "Gb/minute": {
        name: {
          singular: 'Gigabit per minute',
          plural: 'Gigabits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e9,
      },
      "Gib/minute": {
        name: {
          singular: 'Gibibit per minute',
          plural: 'Gibibits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 3),
      },
      "Tb/minute": {
        name: {
          singular: 'Terabit per minute',
          plural: 'Terabits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e12,
      },
      "Tib/minute": {
        name: {
          singular: 'Tebibit per minute',
          plural: 'Tebibits per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 4),
      },
      /** Per hour */
      "bit/hour": {
        name: {
          singular: 'bit per hour',
          plural: 'bits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1,
      },
      "Kb/hour": {
        name: {
          singular: 'Kilobit per hour',
          plural: 'Kilobits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e3,
      },
      "Kib/hour": {
        name: {
          singular: 'Kibibit per hour',
          plural: 'Kibibits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1024,
      },
      "Mb/hour": {
        name: {
          singular: 'Megabit per hour',
          plural: 'Megabits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e6,
      },
      "Mib/hour": {
        name: {
          singular: 'Mebibit per hour',
          plural: 'Mebibits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 2),
      },
      "Gb/hour": {
        name: {
          singular: 'Gigabit per hour',
          plural: 'Gigabits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e9,
      },
      "Gib/hour": {
        name: {
          singular: 'Gibibit per hour',
          plural: 'Gibibits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 3),
      },
      "Tb/hour": {
        name: {
          singular: 'Terabit per hour',
          plural: 'Terabits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e12,
      },
      "Tib/hour": {
        name: {
          singular: 'Tebibit per hour',
          plural: 'Tebibits per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 4),
      },
      /** Per Day */
      "bit/day": {
        name: {
          singular: 'bit per day',
          plural: 'bits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1,
      },
      "Kb/day": {
        name: {
          singular: 'Kilobit per day',
          plural: 'Kilobits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e3,
      },
      "Kib/day": {
        name: {
          singular: 'Kibibit per day',
          plural: 'Kibibits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1024,
      },
      "Mb/day": {
        name: {
          singular: 'Megabit per day',
          plural: 'Megabits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e6,
      },
      "Mib/day": {
        name: {
          singular: 'Mebibit per day',
          plural: 'Mebibits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 2),
      },
      "Gb/day": {
        name: {
          singular: 'Gigabit per day',
          plural: 'Gigabits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e9,
      },
      "Gib/day": {
        name: {
          singular: 'Gibibit per day',
          plural: 'Gibibits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 3),
      },
      "Tb/day": {
        name: {
          singular: 'Terabit per day',
          plural: 'Terabits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e12,
      },
      "Tib/day": {
        name: {
          singular: 'Tebibit per day',
          plural: 'Tebibits per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 4),
      },
      /** Per Month */
      "bit/month": {
        name: {
          singular: 'bit per month',
          plural: 'bits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1,
      },
      "Kb/month": {
        name: {
          singular: 'Kilobit per month',
          plural: 'Kilobits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e3,
      },
      "Kib/month": {
        name: {
          singular: 'Kibibit per month',
          plural: 'Kibibits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1024,
      },
      "Mb/month": {
        name: {
          singular: 'Megabit per month',
          plural: 'Megabits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e6,
      },
      "Mib/month": {
        name: {
          singular: 'Mebibit per month',
          plural: 'Mebibits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 2),
      },
      "Gb/month": {
        name: {
          singular: 'Gigabit per month',
          plural: 'Gigabits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e9,
      },
      "Gib/month": {
        name: {
          singular: 'Gibibit per month',
          plural: 'Gibibits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 3),
      },
      "Tb/month": {
        name: {
          singular: 'Terabit per month',
          plural: 'Terabits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e12,
      },
      "Tib/month": {
        name: {
          singular: 'Tebibit per month',
          plural: 'Tebibits per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 4),
      },
    },
    "Byte/s": {
      "Byte/s": {
        name: {
          singular: 'Byte per second',
          plural: 'Bytes per second',
        },
        to_anchor: 1,
      },
      "KB/s": {
        name: {
          singular: 'Kilobyte per second',
          plural: 'Kilobytes per second',
        },
        to_anchor: 1e3,
      },
      "KiB/s": {
        name: {
          singular: 'Kibibyte per second',
          plural: 'Kibibytes per second',
        },
        to_anchor: 1024,
      },
      "MB/s": {
        name: {
          singular: 'Megabyte per second',
          plural: 'Megabytes per second',
        },
        to_anchor: 1e6,
      },
      "MiB/s": {
        name: {
          singular: 'Mebibyte per second',
          plural: 'Mebibytes per second',
        },
        to_anchor: Math.pow(1024, 2),
      },
      "GB/s": {
        name: {
          singular: 'Gigabyte per second',
          plural: 'Gigabytes per second',
        },
        to_anchor: 1e9,
      },
      "GiB/s": {
        name: {
          singular: 'Gibibyte per second',
          plural: 'Gibibytes per second',
        },
        to_anchor: Math.pow(1024, 3),
      },
      "TB/s": {
        name: {
          singular: 'Terabyte per second',
          plural: 'Terabytes per second',
        },
        to_anchor: 1e12,
      },
      "TiB/s": {
        name: {
          singular: 'Tebibyte per second',
          plural: 'Tebibytes per second',
        },
        to_anchor: Math.pow(1024, 4),
      },
      /**
       * Per minute
       */
      "Byte/minute": {
        name: {
          singular: 'Byte per minute',
          plural: 'Bytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS),
      },
      "KB/minute": {
        name: {
          singular: 'Kilobyte per minute',
          plural: 'Kilobytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e3,
      },
      "KiB/minute": {
        name: {
          singular: 'Kibibyte per minute',
          plural: 'Kibibytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1024,
      },
      "MB/minute": {
        name: {
          singular: 'Megabyte per minute',
          plural: 'Megabytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e6,
      },
      "MiB/minute": {
        name: {
          singular: 'Mebibyte per minute',
          plural: 'Mebibytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 2),
      },
      "GB/minute": {
        name: {
          singular: 'Gigabyte per minute',
          plural: 'Gigabytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e9,
      },
      "GiB/minute": {
        name: {
          singular: 'Gibibyte per minute',
          plural: 'Gibibytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 3),
      },
      "TB/minute": {
        name: {
          singular: 'Terabyte per minute',
          plural: 'Terabytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * 1e12,
      },
      "TiB/minute": {
        name: {
          singular: 'Tebibyte per minute',
          plural: 'Tebibytes per minute',
        },
        to_anchor: (MINUTE_IN_SECONDS) * Math.pow(1024, 4),
      },
      /**
       * Per hour
       */
      "Byte/hour": {
        name: {
          singular: 'Byte per hour',
          plural: 'Bytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS),
      },
      "KB/hour": {
        name: {
          singular: 'Kilobyte per hour',
          plural: 'Kilobytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e3,
      },
      "KiB/hour": {
        name: {
          singular: 'Kibibyte per hour',
          plural: 'Kibibytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1024,
      },
      "MB/hour": {
        name: {
          singular: 'Megabyte per hour',
          plural: 'Megabytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e6,
      },
      "MiB/hour": {
        name: {
          singular: 'Mebibyte per hour',
          plural: 'Mebibytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 2),
      },
      "GB/hour": {
        name: {
          singular: 'Gigabyte per hour',
          plural: 'Gigabytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e9,
      },
      "GiB/hour": {
        name: {
          singular: 'Gibibyte per hour',
          plural: 'Gibibytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 3),
      },
      "TB/hour": {
        name: {
          singular: 'Terabyte per hour',
          plural: 'Terabytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * 1e12,
      },
      "TiB/hour": {
        name: {
          singular: 'Tebibyte per hour',
          plural: 'Tebibytes per hour',
        },
        to_anchor: (HOUR_IN_SECONDS) * Math.pow(1024, 4),
      },
      /**
       * Per day
       */
      "Byte/day": {
        name: {
          singular: 'Byte per day',
          plural: 'Bytes per day',
        },
        to_anchor: (DAY_IN_SECONDS),
      },
      "KB/day": {
        name: {
          singular: 'Kilobyte per day',
          plural: 'Kilobytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e3,
      },
      "KiB/day": {
        name: {
          singular: 'Kibibyte per day',
          plural: 'Kibibytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1024,
      },
      "MB/day": {
        name: {
          singular: 'Megabyte per day',
          plural: 'Megabytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e6,
      },
      "MiB/day": {
        name: {
          singular: 'Mebibyte per day',
          plural: 'Mebibytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 2),
      },
      "GB/day": {
        name: {
          singular: 'Gigabyte per day',
          plural: 'Gigabytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e9,
      },
      "GiB/day": {
        name: {
          singular: 'Gibibyte per day',
          plural: 'Gibibytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 3),
      },
      "TB/day": {
        name: {
          singular: 'Terabyte per day',
          plural: 'Terabytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * 1e12,
      },
      "TiB/day": {
        name: {
          singular: 'Tebibyte per day',
          plural: 'Tebibytes per day',
        },
        to_anchor: (DAY_IN_SECONDS) * Math.pow(1024, 4),
      },
      /**
       * Per month
       */
      "Byte/month": {
        name: {
          singular: 'Byte per month',
          plural: 'Bytes per month',
        },
        to_anchor: (MONTH_IN_SECOND),
      },
      "KB/month": {
        name: {
          singular: 'Kilobyte per month',
          plural: 'Kilobytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e3,
      },
      "KiB/month": {
        name: {
          singular: 'Kibibyte per month',
          plural: 'Kibibytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1024,
      },
      "MB/month": {
        name: {
          singular: 'Megabyte per month',
          plural: 'Megabytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e6,
      },
      "MiB/month": {
        name: {
          singular: 'Mebibyte per month',
          plural: 'Mebibytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 2),
      },
      "GB/month": {
        name: {
          singular: 'Gigabyte per month',
          plural: 'Gigabytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e9,
      },
      "GiB/month": {
        name: {
          singular: 'Gibibyte per month',
          plural: 'Gibibytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 3),
      },
      "TB/month": {
        name: {
          singular: 'Terabyte per month',
          plural: 'Terabytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * 1e12,
      },
      "TiB/month": {
        name: {
          singular: 'Tebibyte per month',
          plural: 'Tebibytes per month',
        },
        to_anchor: (MONTH_IN_SECOND) * Math.pow(1024, 4),
      }
    }
  },
  anchors: {
    "bit/s": {
      "Byte/s": {
        ratio: 1 / 8,
      }
    },
    "Byte/s": {
      "bit/s": {
        ratio: 8,
      },
    },
  },
}

Object.assign(allMeasures.digital, measure);
allMeasures["dataTransferRate"] = dataTransferRate

// --- xconvert unit additions (2026-07-09) — MUST mirror unit-converter-ssr/program/convert-utils.ts ---
// The frontend has its own convert instance; without these it throws "Unsupported unit imp-gal".
Object.assign((allMeasures as any).volume.systems.metric, {
  'imp-gal':    { name: { singular: 'Imperial Gallon',      plural: 'Imperial Gallons' },      to_anchor: 4.54609 },
  'imp-qt':     { name: { singular: 'Imperial Quart',       plural: 'Imperial Quarts' },       to_anchor: 1.1365225 },
  'imp-pnt':    { name: { singular: 'Imperial Pint',        plural: 'Imperial Pints' },        to_anchor: 0.56826125 },
  'imp-fl-oz':  { name: { singular: 'Imperial Fluid Ounce', plural: 'Imperial Fluid Ounces' }, to_anchor: 0.0284130625 },
  'glass':      { name: { singular: 'Glass',                plural: 'Glasses' },               to_anchor: 0.240 },
  'board-foot': { name: { singular: 'Board Foot',           plural: 'Board Feet' },            to_anchor: 2.359737216 },
  'acre-foot':  { name: { singular: 'Acre-Foot',            plural: 'Acre-Feet' },             to_anchor: 1233481.837547 },
});
Object.assign((allMeasures as any).mass.systems.imperial, {
  'long-ton':   { name: { singular: 'Long Ton',            plural: 'Long Tons' },              to_anchor: 2240 },
});
Object.assign((allMeasures as any).volumeFlowRate.systems.metric, {
  'imp-gal/s':   { name: { singular: 'Imperial Gallon per Second', plural: 'Imperial Gallons per Second' }, to_anchor: 4.54609 },
  'imp-gal/min': { name: { singular: 'Imperial Gallon per Minute', plural: 'Imperial Gallons per Minute' }, to_anchor: 4.54609 / 60 },
  'imp-gal/h':   { name: { singular: 'Imperial Gallon per Hour',   plural: 'Imperial Gallons per Hour' },   to_anchor: 4.54609 / 3600 },
  'imp-gal/d':   { name: { singular: 'Imperial Gallon per Day',    plural: 'Imperial Gallons per Day' },    to_anchor: 4.54609 / 86400 },
});
Object.assign((allMeasures as any).power.systems.metric, {
  'Btu/h': { name: { singular: 'British thermal unit per hour', plural: 'British thermal units per hour' }, to_anchor: 0.29307107017 },
  'RT':    { name: { singular: 'Ton of Refrigeration',         plural: 'Tons of Refrigeration' },           to_anchor: 3516.8528421 },
});


// ===== New measures & units (Tier-2 adds + Tier-1/3 measures) — see docs/measure-integration-design.md =====
// Tier-2 unit-adds (exact SI; to_anchor computed vs each system anchor)
Object.assign((allMeasures.mass as any).systems.imperial, {
  ozt: { name: { singular: 'Troy Ounce', plural: 'Troy Ounces' }, to_anchor: 0.06857142857142856 },
  gr: { name: { singular: 'Grain', plural: 'Grains' }, to_anchor: 0.00014285714285714287 },
  'cwt-us': { name: { singular: 'US Hundredweight', plural: 'US Hundredweights' }, to_anchor: 100 },
  'cwt-uk': { name: { singular: 'UK Hundredweight', plural: 'UK Hundredweights' }, to_anchor: 111.99999999999999 },
  slug: { name: { singular: 'Slug', plural: 'Slugs' }, to_anchor: 32.17404855642964 },
});
Object.assign((allMeasures.mass as any).systems.metric, {
  ct: { name: { singular: 'Carat', plural: 'Carats' }, to_anchor: 0.2 },
});
Object.assign((allMeasures.pressure as any).systems.metric, {
  atm: { name: { singular: 'standard atmosphere', plural: 'standard atmospheres' }, to_anchor: 101.325 },
  cmH2O: { name: { singular: 'centimeter of water', plural: 'centimeters of water' }, to_anchor: 0.0980665 },
  at: { name: { singular: 'technical atmosphere', plural: 'technical atmospheres' }, to_anchor: 98.0665 },
  cmHg: { name: { singular: 'centimeter of mercury', plural: 'centimeters of mercury' }, to_anchor: 1.33322387415 },
});
Object.assign((allMeasures.volume as any).systems.imperial, {
  bbl: { name: { singular: 'US Oil Barrel', plural: 'US Oil Barrels' }, to_anchor: 5376.000000000001 },
  'gal-dry': { name: { singular: 'US Dry Gallon', plural: 'US Dry Gallons' }, to_anchor: 148.94683982683983 },
  'qt-dry': { name: { singular: 'US Dry Quart', plural: 'US Dry Quarts' }, to_anchor: 37.23670995670996 },
  'pnt-dry': { name: { singular: 'US Dry Pint', plural: 'US Dry Pints' }, to_anchor: 18.61835497835498 },
  bu: { name: { singular: 'US Bushel', plural: 'US Bushels' }, to_anchor: 1191.5747186147187 },
  pk: { name: { singular: 'US Peck', plural: 'US Pecks' }, to_anchor: 297.89367965367967 },
  'fl-dr': { name: { singular: 'US Fluid Dram', plural: 'US Fluid Drams' }, to_anchor: 0.125 },
});
Object.assign((allMeasures.energy as any).systems.SI, {
  BTU: { name: { singular: 'British Thermal Unit (IT)', plural: 'British Thermal Units (IT)' }, to_anchor: 1055.05585262 },
  thm: { name: { singular: 'US Therm', plural: 'US Therms' }, to_anchor: 105480400 },
  eV: { name: { singular: 'Electronvolt', plural: 'Electronvolts' }, to_anchor: 1.602176634e-19 },
  erg: { name: { singular: 'Erg', plural: 'Ergs' }, to_anchor: 1e-7 },
  'ft-lbf': { name: { singular: 'Foot-pound', plural: 'Foot-pounds' }, to_anchor: 1.3558179483314003 },
  't-tnt': { name: { singular: 'Tonne of TNT', plural: 'Tonnes of TNT' }, to_anchor: 4184000000 },
});
Object.assign((allMeasures.length as any).systems.metric, {
  ly: { name: { singular: 'light-year', plural: 'light-years' }, to_anchor: 9460730472580800 },
  au: { name: { singular: 'astronomical unit', plural: 'astronomical units' }, to_anchor: 149597870700 },
  pc: { name: { singular: 'parsec', plural: 'parsecs' }, to_anchor: 30856775814913670 },
  angstrom: { name: { singular: 'ångström', plural: 'ångströms' }, to_anchor: 1e-10 },
});
Object.assign((allMeasures.length as any).systems.imperial, {
  ch: { name: { singular: 'chain', plural: 'chains' }, to_anchor: 66 },
  rd: { name: { singular: 'rod', plural: 'rods' }, to_anchor: 16.5 },
  fur: { name: { singular: 'furlong', plural: 'furlongs' }, to_anchor: 660 },
  hh: { name: { singular: 'hand', plural: 'hands' }, to_anchor: 0.3333333333333333 },
});

// Tier-1/3 new measures — single-system (to_anchor = exact SI, no cross-anchor)
const _density: any = { systems: { metric: {
  'kg/m3': { name: { singular: 'kilogram per cubic meter', plural: 'kilograms per cubic meter' }, to_anchor: 1 },
  'g/cm3': { name: { singular: 'gram per cubic centimeter', plural: 'grams per cubic centimeter' }, to_anchor: 1000 },
  'g/mL': { name: { singular: 'gram per milliliter', plural: 'grams per milliliter' }, to_anchor: 1000 },
  'g/L': { name: { singular: 'gram per liter', plural: 'grams per liter' }, to_anchor: 1 },
  'mg/mL': { name: { singular: 'milligram per milliliter', plural: 'milligrams per milliliter' }, to_anchor: 1 },
  'kg/L': { name: { singular: 'kilogram per liter', plural: 'kilograms per liter' }, to_anchor: 1000 },
  't/m3': { name: { singular: 'tonne per cubic meter', plural: 'tonnes per cubic meter' }, to_anchor: 1000 },
  'lb/ft3': { name: { singular: 'pound per cubic foot', plural: 'pounds per cubic foot' }, to_anchor: 16.018463373960138 },
  'lb/in3': { name: { singular: 'pound per cubic inch', plural: 'pounds per cubic inch' }, to_anchor: 27679.904710203125 },
  'lb/yd3': { name: { singular: 'pound per cubic yard', plural: 'pounds per cubic yard' }, to_anchor: 0.5932764212577829 },
  'lb/gal': { name: { singular: 'pound per US gallon', plural: 'pounds per US gallon' }, to_anchor: 119.82642731689664 },
  'oz/in3': { name: { singular: 'ounce per cubic inch', plural: 'ounces per cubic inch' }, to_anchor: 1729.9940443876953 },
  'oz/gal': { name: { singular: 'ounce per US gallon', plural: 'ounces per US gallon' }, to_anchor: 7.48915170730604 },
  'slug/ft3': { name: { singular: 'slug per cubic foot', plural: 'slugs per cubic foot' }, to_anchor: 515.3788183931961 },
} } };
const _dynamicViscosity: any = { systems: { metric: {
  'Pa-s': { name: { singular: 'pascal-second', plural: 'pascal-seconds' }, to_anchor: 1 },
  'mPa-s': { name: { singular: 'millipascal-second', plural: 'millipascal-seconds' }, to_anchor: 0.001 },
  P: { name: { singular: 'poise', plural: 'poises' }, to_anchor: 0.1 },
  cP: { name: { singular: 'centipoise', plural: 'centipoises' }, to_anchor: 0.001 },
  'lbf-s/ft2': { name: { singular: 'pound-force-second per square foot', plural: 'pound-force-seconds per square foot' }, to_anchor: 47.88025898033584 },
  'lb/(ft-s)': { name: { singular: 'pound per foot-second', plural: 'pounds per foot-second' }, to_anchor: 1.4881639435695537 },
} } };
const _kinematicViscosity: any = { systems: { metric: {
  'm2/s': { name: { singular: 'square metre per second', plural: 'square metres per second' }, to_anchor: 1 },
  'mm2/s': { name: { singular: 'square millimetre per second', plural: 'square millimetres per second' }, to_anchor: 0.000001 },
  St: { name: { singular: 'stokes', plural: 'stokes' }, to_anchor: 0.0001 },
  cSt: { name: { singular: 'centistokes', plural: 'centistokes' }, to_anchor: 0.000001 },
  'ft2/s': { name: { singular: 'square foot per second', plural: 'square feet per second' }, to_anchor: 0.09290304 },
} } };
const _luminance: any = { systems: { metric: {
  'cd/m2': { name: { singular: 'candela per square metre', plural: 'candelas per square metre' }, to_anchor: 1 },
  nt: { name: { singular: 'nit', plural: 'nits' }, to_anchor: 1 },
  sb: { name: { singular: 'stilb', plural: 'stilbs' }, to_anchor: 10000 },
  asb: { name: { singular: 'apostilb', plural: 'apostilbs' }, to_anchor: 0.3183098861837907 },
  L: { name: { singular: 'lambert', plural: 'lamberts' }, to_anchor: 3183.098861837907 },
  fL: { name: { singular: 'foot-lambert', plural: 'foot-lamberts' }, to_anchor: 3.42625909963539 },
} } };
const _magneticFluxDensity: any = { systems: { metric: {
  T: { name: { singular: 'tesla', plural: 'teslas' }, to_anchor: 1 },
  mT: { name: { singular: 'millitesla', plural: 'milliteslas' }, to_anchor: 0.001 },
  uT: { name: { singular: 'microtesla', plural: 'microteslas' }, to_anchor: 0.000001 },
  G: { name: { singular: 'gauss', plural: 'gauss' }, to_anchor: 0.0001 },
  mG: { name: { singular: 'milligauss', plural: 'milligauss' }, to_anchor: 1e-7 },
} } };
const _magneticFlux: any = { systems: { metric: {
  Wb: { name: { singular: 'weber', plural: 'webers' }, to_anchor: 1 },
  mWb: { name: { singular: 'milliweber', plural: 'milliwebers' }, to_anchor: 0.001 },
  Mx: { name: { singular: 'maxwell', plural: 'maxwells' }, to_anchor: 1e-8 },
} } };
const _absorbedDose: any = { systems: { metric: {
  Gy: { name: { singular: 'Gray', plural: 'Grays' }, to_anchor: 1 },
  mGy: { name: { singular: 'Milligray', plural: 'Milligrays' }, to_anchor: 0.001 },
  Rad: { name: { singular: 'Rad', plural: 'Rads' }, to_anchor: 0.01 },
  mRad: { name: { singular: 'Millirad', plural: 'Millirads' }, to_anchor: 0.00001 },
} } };
const _doseEquivalent: any = { systems: { metric: {
  Sv: { name: { singular: 'Sievert', plural: 'Sieverts' }, to_anchor: 1 },
  mSv: { name: { singular: 'Millisievert', plural: 'Millisieverts' }, to_anchor: 0.001 },
  uSv: { name: { singular: 'Microsievert', plural: 'Microsieverts' }, to_anchor: 0.000001 },
  rem: { name: { singular: 'Rem', plural: 'Rems' }, to_anchor: 0.01 },
  mrem: { name: { singular: 'Millirem', plural: 'Millirems' }, to_anchor: 0.00001 },
} } };
const _radioactivity: any = { systems: { metric: {
  Bq: { name: { singular: 'Becquerel', plural: 'Becquerels' }, to_anchor: 1 },
  kBq: { name: { singular: 'Kilobecquerel', plural: 'Kilobecquerels' }, to_anchor: 1000 },
  MBq: { name: { singular: 'Megabecquerel', plural: 'Megabecquerels' }, to_anchor: 1000000 },
  Ci: { name: { singular: 'Curie', plural: 'Curies' }, to_anchor: 37000000000 },
  mCi: { name: { singular: 'Millicurie', plural: 'Millicuries' }, to_anchor: 37000000 },
  uCi: { name: { singular: 'Microcurie', plural: 'Microcuries' }, to_anchor: 37000 },
} } };
const _molarConcentration: any = { systems: { metric: {
  'mol/m3': { name: { singular: 'mole per cubic metre', plural: 'moles per cubic metre' }, to_anchor: 1 },
  'mol/L': { name: { singular: 'mole per litre', plural: 'moles per litre' }, to_anchor: 1000 },
  mM: { name: { singular: 'millimolar', plural: 'millimolars' }, to_anchor: 1 },
  uM: { name: { singular: 'micromolar', plural: 'micromolars' }, to_anchor: 0.001 },
  nM: { name: { singular: 'nanomolar', plural: 'nanomolars' }, to_anchor: 0.000001 },
  'mmol/L': { name: { singular: 'millimole per litre', plural: 'millimoles per litre' }, to_anchor: 1 },
} } };
const _thermalConductivity: any = { systems: { metric: {
  'W/(m-K)': { name: { singular: 'Watt per Metre-Kelvin', plural: 'Watts per Metre-Kelvin' }, to_anchor: 1 },
  'mW/(m-K)': { name: { singular: 'Milliwatt per Metre-Kelvin', plural: 'Milliwatts per Metre-Kelvin' }, to_anchor: 0.001 },
  'BTU/(h-ft-°F)': { name: { singular: 'BTU per Hour-Foot-Degree Fahrenheit', plural: 'BTU per Hour-Foot-Degree Fahrenheit' }, to_anchor: 1.730734666 },
  'BTU-in/(h-ft2-°F)': { name: { singular: 'BTU-Inch per Hour-Square Foot-Degree Fahrenheit', plural: 'BTU-Inch per Hour-Square Foot-Degree Fahrenheit' }, to_anchor: 0.144227889 },
  'cal/(s-cm-°C)': { name: { singular: 'Calorie per Second-Centimetre-Degree Celsius', plural: 'Calories per Second-Centimetre-Degree Celsius' }, to_anchor: 418.4 },
} } };
allMeasures['density'] = _density;
allMeasures['dynamicViscosity'] = _dynamicViscosity;
allMeasures['kinematicViscosity'] = _kinematicViscosity;
allMeasures['luminance'] = _luminance;
allMeasures['magneticFluxDensity'] = _magneticFluxDensity;
allMeasures['magneticFlux'] = _magneticFlux;
allMeasures['absorbedDose'] = _absorbedDose;
allMeasures['doseEquivalent'] = _doseEquivalent;
allMeasures['radioactivity'] = _radioactivity;
allMeasures['molarConcentration'] = _molarConcentration;
allMeasures['thermalConductivity'] = _thermalConductivity;
// ===== end new measures =====

// --- Correct convert-units' rounded cross-system constants (data-layer fix) ---
// The library anchors metric<->imperial with single rounded decimals (e.g. length ratio
// 3.28084), so reverse/derived conversions inherit an error at the ~8th significant figure
// (1 ft resolved to 0.3047999902464 m instead of the exact 0.3048; 1 in3 to 16.386988 ml
// instead of 16.387064). We pin the affected anchors — and the rounded cubic-inch volume
// unit ratios — to values derived from the exact SI definitions (1 in = 0.0254 m,
// 1 lb = 0.45359237 kg, g0 = 9.80665 m/s^2). Values become exact to double precision; the
// 7-significant-figure display layer (MathUtils.roundNumber) handles genuine irrational
// reciprocals (m->ft = 3.280839895) and residual FP epsilon.
{
  const INCH = 0.0254, LB = 0.45359237, G0 = 9.80665;
  const FT = 12 * INCH;                                  // 0.3048 m (exact)
  const LBF = G0 * LB;                                   // 4.4482216152605 N (exact)
  const FLOZ_DM3 = 231 * Math.pow(INCH * 10, 3) / 128;  // US fl-oz in dm3 (gal = 231 in3 = 128 fl-oz)
  const FTLBF = FT * LBF;                                // ft-lbf in J
  const KSI_KPA = LBF / (INCH * INCH);                  // 1 ksi = 1000 psi, expressed in kPa
  const setAnchor = (m: string, impPerMet: number) => {
    const a = (allMeasures as any)[m].anchors;
    a.imperial.metric.ratio = impPerMet;
    a.metric.imperial.ratio = 1 / impPerMet;
  };
  setAnchor('length', FT);
  setAnchor('area', FT * FT);
  setAnchor('volume', FLOZ_DM3);
  setAnchor('volumeFlowRate', FLOZ_DM3);
  setAnchor('power', FTLBF);
  setAnchor('pressure', KSI_KPA);
  // Rounded cubic-inch-derived volume unit ratios (relative to the fl-oz anchor).
  const IN3 = 128 / 231, FT3 = 1728 * IN3, YD3 = 27 * FT3;
  const V = (allMeasures as any).volume.systems.imperial;
  V.in3.to_anchor = IN3; V.ft3.to_anchor = FT3; V.yd3.to_anchor = YD3;
  const VF = (allMeasures as any).volumeFlowRate.systems.imperial;
  const TIME: { [k: string]: number } = { '/s': 1, '/min': 1 / 60, '/h': 1 / 3600 };
  for (const [base, ratio] of [['in3', IN3], ['ft3', FT3], ['yd3', YD3]] as [string, number][])
    for (const per of Object.keys(TIME))
      if (VF[base + per]) VF[base + per].to_anchor = ratio * TIME[per];

  // More measures with the same rounded cross-system anchor: illuminance (ft-cd), mass-flow
  // (lb/s), torque (lbf-ft). Fixing the anchor corrects those imperial-anchor units + derived.
  setAnchor('illuminance', 1 / (FT * FT));   // ft-cd -> lx = 1/0.09290304 = 10.7639104167
  setAnchor('massFlowRate', LB);             // lb/s -> kg/s = 0.45359237
  setAnchor('torque', FT * LBF);             // lbf-ft -> Nm = 1.3558179483314

  // Rounded customary units in flat / self-anchored measures — pin each to_anchor to exact SI.
  const KMH_PER_MPH = 1.609344;                       // exact (mile / hour)
  const toMph = (v: number) => v * 3.6 / KMH_PER_MPH; // m/s -> mph (speed's imperial anchor)
  const sp = (allMeasures as any).speed.systems.imperial;
  sp['ft/s'].to_anchor = toMph(FT);
  sp['ft/min'].to_anchor = toMph(FT / 60);
  sp['in/h'].to_anchor = toMph(INCH / 3600);
  sp['knot'].to_anchor = toMph(1852 / 3600);          // 1 knot = 1 nautical mile / hour
  const fSI = (allMeasures as any).force.systems.SI;
  fSI.lbf.to_anchor = LBF;                            // 4.4482216152605 N
  fSI.kgf.to_anchor = G0;                             // 9.80665 N (kilogram-force)
  (allMeasures as any).length.systems.imperial.nMi.to_anchor = 1852 / FT;                  // int'l nautical mile = 1852 m, in ft
  (allMeasures as any).pressure.systems.metric.mmHg.to_anchor = 133.322387415 / 1000;      // conventional mmHg, in kPa
  (allMeasures as any).pressure.systems.imperial.inHg.to_anchor = (25.4 * 133.322387415) / (1000 * KSI_KPA); // conventional inHg (25.4 mmHg), in ksi
  (allMeasures as any).power.systems.imperial['Btu/s'].to_anchor = 1055.05585262 / FTLBF;  // IT BTU, in ft-lb/s
  (allMeasures as any).pace.systems.imperial['min/mi'].to_anchor = 60 / 5280;               // 1 min/mile = 60 s per 5280 ft, in s/ft
}

const convert = configureMeasurements({
  ...allMeasures,
});
if (typeof window != "undefined") {
  (window as any)["convert"] = convert
}

export default convert;

export namespace ConvertUtils {
  export function formatAbbr(abbr: string) {
    return abbr.replace(/(\d+)/, '^$1')
  }

  export function convertTo(number: number, from: any, to: any) {
    return MathUtils.roundNumber(
      convert(number).from(from).to(to)
    );
  }
}
